<template>
  <div class="grid grid-cols-12 gap-6 items-center text-dark-neutral-500 group w-full">
    <PlanLockGuard class="col-span-3">
      <div class="flex items-center justify-between gap-1">
        <TextInput
          v-model="updatedAttribute.label"
          :disabled="review.isPlanReadonly.value"
          placeholder="Etiology"
          input-classes="w-full border-none group-hover:bg-light-neutral-300"
          @enter="editAttributeStructure()"
          @blur="editAttributeStructure()"
        />
        <span v-if="!updatedAttribute.label" title="Required">
          <AsteriskIcon class="text-amber-500" />
        </span>
      </div>
    </PlanLockGuard>

    <PlanLockGuard class="col-span-5 pl-4 w-full">
      <FloatingMenu placement="bottom">
        <template #activator="{ show }">
          <button
            class="w-full text-left truncate flex items-center gap-1 justify-between"
            :class="updatedAttribute.question ? 'text-dark-neutral-400' : 'text-dark-neutral-300'"
            :disabled="review.isPlanReadonly.value"
            @click="handleShow(show)"
          >
            <span class="truncate">{{ updatedAttribute.question || "Add a question..." }}</span>
            <span v-if="!updatedAttribute.question" title="Required">
              <AsteriskIcon class="text-amber-500" />
            </span>
          </button>
        </template>

        <template #content>
          <div class="w-full">
            <TextInput
              ref="questionInput"
              v-model="updatedAttribute.question"
              :disabled="review.isPlanReadonly.value"
              multiline
              class="w-full"
              input-classes=" h-18 border-none   "
              placeholder="What is the cause or origin of the disease?"
              @enter="editAttributeStructure()"
              @blur="editAttributeStructure()"
            />
          </div>
        </template>
      </FloatingMenu>
    </PlanLockGuard>

    <div class="col-span-3 flex items-center text-dark-neutral-500">
      {{ getTypeLabel(updatedAttribute.type) }}
    </div>

    <div class="col-span-1 flex justify-end">
      <PlanLockGuard>
        <FloatingMenu>
          <template #activator="{ show, isShown }">
            <button
              class="transition"
              :class="{
                'invisible group-hover:visible': !isShown && !review.isPlanReadonly.value,
                visible: isShown,
                'text-red-600 hover:text-red-600': !review.isPlanReadonly.value,
                'text-gray-300': review.isPlanReadonly.value,
              }"
              :disabled="review.isPlanReadonly.value"
              @click="show"
            >
              <TrashIcon class="size-5" />
            </button>
          </template>
          <template #content="{ hide }">
            <div class="p-4 space-y-2">
              <p>Are you sure you want to delete this search?</p>
              <div>
                <button
                  class="bg-neutral-500 hover:bg-neutral-600 text-white py-1 px-2 mr-1"
                  @click.stop="hide"
                >
                  Cancel
                </button>
                <button
                  class="bg-red-500 hover:bg-red-600 text-white py-1 px-2"
                  @click.stop="emit('deleteAttributeStructure', structure.id)"
                >
                  Delete
                </button>
              </div>
            </div>
          </template>
        </FloatingMenu>
      </PlanLockGuard>
    </div>
  </div>
</template>
<script setup lang="ts">
import { AttributeStructure } from "@core/domain/models/data-extraction-plan-attribute";
import { AttributeStructureType } from "@core/domain/types/attributeStructureType.type";
import { nextTick, ref, toRaw } from "vue";
import { injectStrict } from "@app/utils/injectStrict";
import { ReviewKey } from "../../../use-review";
import useLoading from "@app/composables/use-loading";
import useSnackbar from "@app/composables/use-snackbar";
import { SnackbarState } from "@app/types";
import { HttpException } from "@core/exceptions/http.exception";
import { errorMessage } from "@app/utils/error-message";
import TextInput from "@app/components/Global/Inputs/TextInput.vue";
import TrashIcon from "@app/components/Icons/TrashIcon.vue";
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";
import PlanLockGuard from "../../PlanLockGuard.vue";
import AsteriskIcon from "@app/components/Icons/AsteriskIcon.vue";

const props = defineProps<{
  structure: AttributeStructure;
  questionId: number;
}>();

const typeOptions = [
  { value: AttributeStructureType.Qualitative, label: "Qualitative" },
  { value: "quantitative", label: "Quantitative" },
  {
    value: AttributeStructureType.MedicalAlternatives,
    label: "Medical Alternatives",
  },
];

function getTypeLabel(type: string): string {
  return typeOptions.find((opt) => opt.value === type)?.label ?? "Qualitative";
}

const questionInput = ref<InstanceType<typeof TextInput>>();

const emit = defineEmits<(e: "deleteAttributeStructure", structureId: string) => void>();
const review = injectStrict(ReviewKey);
const loading = useLoading();
const snackbar = useSnackbar();
const updatedAttribute = ref<AttributeStructure>(structuredClone(toRaw(props.structure)));

async function editAttributeStructure() {
  try {
    loading.start();
    await review.editAttributeStructure(review.entity.value.id, updatedAttribute.value);

    snackbar.success("Success");
  } catch (e) {
    const error = e as HttpException;
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data));
    if (error.response.data.statusCode >= 500) {
      throw e;
    }
  } finally {
    loading.stop();
  }
}

function handleShow(show: () => void) {
  show();

  nextTick(() => {
    questionInput.value?.focus?.();
  });
}
</script>
